import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api'
import { wsService, subscriptions } from '../services/websocket-unified'
import { QUERY_KEYS } from '../lib/react-query'
import type { NodeInfo } from '../types'
import { useEffect } from 'react';

/**
 * Hook for managing nodes data with real-time updates
 */
export function useNodes() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEYS.allNodes(),
    queryFn: async (): Promise<NodeInfo[]> => {
      const response = await apiClient.getNodes();

      if (!response.success) {
        throw new Error(response.error);
      }
      // GET nodes answers { nodes: NodeInfo[], total } (socket-cloud.openapi.yaml).
      // The cache holds each NodeInfo as the API sends it.
      return response.data?.nodes ?? [];
    },
    refetchInterval: 30 * 1000, // Refetch every 30 seconds as fallback
  });

  // Set up real-time updates. A node:status event names its node by nodeId
  // (socket-cloud.openapi.yaml NodeStatusSocketEvent), the key each cached
  // NodeInfo carries. The event marks the list and that node stale and their
  // queries refetch them.
  useEffect(() => {
    const unsubscribe = subscriptions.onNodeStatus((event) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allNodes(), exact: true });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.node(event.nodeId) });
    });

    return unsubscribe;
  }, [queryClient]);

  return query;
}

/**
 * Hook for managing individual node data
 */
export function useNode(nodeId: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEYS.node(nodeId),
    queryFn: async () => {
      const response = await apiClient.getNode(nodeId);
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
    enabled: !!nodeId,
    refetchInterval: 15 * 1000, // More frequent for individual node
  });

  // Set up real-time updates for this specific node
  useEffect(() => {
    if (!nodeId) {
return;
}

    const unsubscribe = subscriptions.onNodeStatus((event) => {
      if (event.nodeId === nodeId) {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.node(nodeId) });
      }
    });

    return unsubscribe;
  }, [nodeId, queryClient]);

  return query;
}

/**
 * Hook for node metrics with real-time updates
 */
export function useNodeMetrics(nodeId: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEYS.nodeMetrics(nodeId),
    queryFn: async () => {
      const response = await apiClient.getMetrics(nodeId);
      if (!response.success) {
        throw new Error(response.error);
      }
      return Array.isArray(response.data) ? response.data : ((response.data as any)?.data || []);
    },
    enabled: !!nodeId,
    refetchInterval: 30 * 1000, // Reduced frequency for better performance
  });

  // Real-time metrics updates
  useEffect(() => {
    if (!nodeId) {
return;
}

    const unsubscribe = subscriptions.onMetrics((metricsData: any) => {
      if (metricsData.nodeId === nodeId) {
        queryClient.setQueryData(QUERY_KEYS.nodeMetrics(nodeId), (oldData: any[] | undefined) => {
          const newData = [...(oldData || []), metricsData];
          // Keep only last 100 data points to prevent memory issues
          return newData.slice(-100);
        });
      }
    });

    return unsubscribe;
  }, [nodeId, queryClient]);

  return query;
}

/**
 * Hook for updating node configuration
 */
export function useUpdateNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ nodeId, updates }: { nodeId: string; updates: Partial<NodeInfo> }) => {
      const response = await apiClient.updateNode(nodeId, updates);
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: (updatedNode) => {
      // Update the nodes list
      queryClient.setQueryData(QUERY_KEYS.allNodes(), (oldData: NodeInfo[] | undefined) => {
        if (!oldData) {
return oldData;
}
        return oldData.map(node => 
          node.nodeId === updatedNode!.nodeId ? updatedNode! : node,
        );
      });

      // Update individual node cache
      queryClient.setQueryData(QUERY_KEYS.node(updatedNode!.nodeId), updatedNode);
    },
    onError: (error) => {
      console.error('Failed to update node:', error);
    },
  });
}

/**
 * Hook for removing nodes
 */
export function useRemoveNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (nodeId: string) => {
      const response = await apiClient.removeNode(nodeId);
      if (!response.success) {
        throw new Error(response.error);
      }
    },
    onSuccess: (_, nodeId) => {
      // Remove from nodes list
      queryClient.setQueryData(QUERY_KEYS.allNodes(), (oldData: NodeInfo[] | undefined) => {
        if (!oldData) {
return oldData;
}
        return oldData.filter(node => node.nodeId !== nodeId);
      });

      // Remove individual node cache
      queryClient.removeQueries({ queryKey: QUERY_KEYS.node(nodeId) });
      queryClient.removeQueries({ queryKey: QUERY_KEYS.nodeMetrics(nodeId) });
    },
    onError: (error) => {
      console.error('Failed to remove node:', error);
    },
  });
}

/**
 * Node counts by socket-cloud NodeStatus (healthy, degraded, offline)
 */
export function useNodeStats() {
  const { data: nodes = [] } = useNodes();

  const stats = {
    total: nodes.length,
    healthy: nodes.filter((node) => node.status === 'healthy').length,
    degraded: nodes.filter((node) => node.status === 'degraded').length,
    offline: nodes.filter((node) => node.status === 'offline').length,
  };

  const healthPercentage = stats.total > 0 
    ? Math.round((stats.healthy / stats.total) * 100) 
    : 0;

  return {
    ...stats,
    healthPercentage,
  };
}

/**
 * Hook for checking WebSocket connection status
 */
export function useConnectionStatus() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['connection-status'],
    queryFn: () => ({
      connected: wsService.isConnected(),
      stats: wsService.getStats(),
    }),
    refetchInterval: 5000, // Check every 5 seconds instead of every second
  });

  // Listen for connection changes
  useEffect(() => {
    const unsubscribe = subscriptions.onConnectionStatus((status) => {
      queryClient.setQueryData(['connection-status'], (oldData: any) => ({
        ...oldData,
        connected: status.connected,
        stats: wsService.getStats(),
      }));
    });

    return unsubscribe;
  }, [queryClient]);

  return query;
}